import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';

import { ITabletSubmission } from '../Interfaces/ITabletSubmission';
import { statusLabel } from './tabletSubmissionLabels';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Alle Erfassungen dieses Beteiligten — neueste zuerst erwartet. */
  submissions: ITabletSubmission[];
  participantName: string;
}

/**
 * Gegenüberstellung zweier Erfassungen desselben Beteiligten.
 *
 * Der Ident-Service prüft in zwei Schritten: vorab die Selbsteinreichung im
 * Mandantenportal, beim Termin noch einmal am Tablet. Beide Stände müssen auch
 * NACH der Übernahme nebeneinander lesbar bleiben — sonst lässt sich nicht
 * beurteilen, ob der Mandant am Tablet dasselbe angegeben hat wie vorab.
 * Abweichungen sind hier der eigentliche Befund und deshalb hervorgehoben.
 */

const PERSON_LABEL: Record<string, string> = {
  FirstName: 'Vorname',
  LastName: 'Nachname',
  BirthDate: 'Geburtsdatum',
  BirthName: 'Geburtsname',
  BirthPlace: 'Geburtsort',
  Nationality: 'Staatsangehörigkeit',
  DocumentNumber: 'Ausweis-Nr.',
  DocumentExpiry: 'Gültig bis',
};

const ADDRESS_LABEL: Record<string, string> = {
  Street: 'Straße',
  HouseNumber: 'Hausnummer',
  Zip: 'PLZ',
  City: 'Ort',
  Country: 'Land',
};

const CONTACT_LABEL: Record<string, string> = {
  Phone: 'Telefon',
  Email: 'E-Mail',
};

const METHOD_LABEL: Record<string, string> = {
  nfc: 'NFC (eID)',
  ocr: 'OCR aus Foto',
  manual: 'Manuelle Eingabe',
  foto: 'Foto + manuelle Eingabe',
};

type AnyPayload = Record<
  string,
  Record<string, string | undefined> | undefined
>;

function parsePayload(value: unknown): AnyPayload {
  if (!value) return {};
  if (typeof value === 'object') return value as AnyPayload;
  try {
    return JSON.parse(String(value)) as AnyPayload;
  } catch {
    return {};
  }
}

function formatDateTime(value?: string | null): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('de-DE');
}

/** Kurzbeschreibung einer Erfassung für die Auswahl im Kopf. */
function submissionCaption(s: ITabletSubmission): string {
  const source = s.Source === 'portal' ? 'Portal' : 'Tablet';
  const when = s.SubmittedAt ?? s.Created_at;
  return `${source} — ${statusLabel(s.Status, s.Source)} — ${formatDateTime(
    when
  )}`;
}

export const TabletSubmissionCompareDialog: React.FC<Props> = ({
  open,
  onClose,
  submissions,
  participantName,
}) => {
  const [leftId, setLeftId] = useState<number | ''>('');
  const [rightId, setRightId] = useState<number | ''>('');

  // Vorbelegung: die beiden jüngsten Erfassungen, möglichst aus
  // unterschiedlichen Quellen — das ist der Vergleich, um den es geht.
  useEffect(() => {
    if (!open || submissions.length === 0) return;
    const newest = submissions[0];
    const counterpart =
      submissions.find(s => s.Source !== newest.Source) ?? submissions[1];
    setLeftId(counterpart?.idTabletSubmission ?? newest.idTabletSubmission);
    setRightId(newest.idTabletSubmission);
  }, [open, submissions]);

  const left = useMemo(
    () => submissions.find(s => s.idTabletSubmission === leftId) ?? null,
    [submissions, leftId]
  );
  const right = useMemo(
    () => submissions.find(s => s.idTabletSubmission === rightId) ?? null,
    [submissions, rightId]
  );

  const leftPayload = useMemo(() => parsePayload(left?.Payload), [left]);
  const rightPayload = useMemo(() => parsePayload(right?.Payload), [right]);

  function renderSection(
    title: string,
    group: string,
    labels: Record<string, string>
  ) {
    const rows = Object.keys(labels);
    return (
      <React.Fragment key={`section-${group}`}>
        <TableRow>
          <TableCell colSpan={3} sx={{ borderBottom: 'none', pt: 2 }}>
            <Typography variant='subtitle2'>{title}</Typography>
          </TableCell>
        </TableRow>
        {rows.map(field => {
          const a = (leftPayload[group]?.[field] ?? '').toString().trim();
          const b = (rightPayload[group]?.[field] ?? '').toString().trim();
          const differs = a !== b;
          // Zeilen ohne jeden Wert weglassen — sie sagen nichts aus und
          // machen die Gegenüberstellung nur lang.
          if (!a && !b) return null;
          return (
            <TableRow
              key={`${group}-${field}`}
              sx={
                differs
                  ? { backgroundColor: 'warning.light', opacity: 0.95 }
                  : undefined
              }
            >
              <TableCell sx={{ width: '25%' }}>{labels[field]}</TableCell>
              <TableCell sx={{ fontWeight: differs ? 600 : 400 }}>
                {a || '–'}
              </TableCell>
              <TableCell sx={{ fontWeight: differs ? 600 : 400 }}>
                {b || '–'}
              </TableCell>
            </TableRow>
          );
        })}
      </React.Fragment>
    );
  }

  const differenceCount = useMemo(() => {
    const groups: [string, Record<string, string>][] = [
      ['Person', PERSON_LABEL],
      ['Address', ADDRESS_LABEL],
      ['Contact', CONTACT_LABEL],
    ];
    let n = 0;
    groups.forEach(([group, labels]) => {
      Object.keys(labels).forEach(field => {
        const a = (leftPayload[group]?.[field] ?? '').toString().trim();
        const b = (rightPayload[group]?.[field] ?? '').toString().trim();
        if (a !== b) n += 1;
      });
    });
    return n;
  }, [leftPayload, rightPayload]);

  function renderHeadCell(s: ITabletSubmission | null) {
    if (!s) return <Typography variant='body2'>–</Typography>;
    return (
      <Stack spacing={0.5}>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {s.Source === 'portal' ? (
            <PublicIcon fontSize='small' />
          ) : (
            <TabletAndroidIcon fontSize='small' />
          )}
          <Typography variant='body2' fontWeight={600}>
            {s.Source === 'portal' ? 'Mandantenportal' : 'Tablet'}
          </Typography>
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          {statusLabel(s.Status, s.Source)} ·{' '}
          {formatDateTime(s.SubmittedAt ?? s.Created_at)}
        </Typography>
        {parsePayload(s.Payload).IdScan?.Method && (
          <Typography variant='caption' color='text.secondary'>
            {METHOD_LABEL[String(parsePayload(s.Payload).IdScan?.Method)] ??
              String(parsePayload(s.Payload).IdScan?.Method)}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>Erfassungen vergleichen — {participantName}</DialogTitle>
      <DialogContent dividers>
        <Stack direction='row' spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            fullWidth
            size='small'
            label='Links'
            value={leftId}
            onChange={e => setLeftId(Number(e.target.value))}
          >
            {submissions.map(s => (
              <MenuItem
                key={`left-${s.idTabletSubmission}`}
                value={s.idTabletSubmission}
              >
                {submissionCaption(s)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            size='small'
            label='Rechts'
            value={rightId}
            onChange={e => setRightId(Number(e.target.value))}
          >
            {submissions.map(s => (
              <MenuItem
                key={`right-${s.idTabletSubmission}`}
                value={s.idTabletSubmission}
              >
                {submissionCaption(s)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Box sx={{ mb: 1 }}>
          {differenceCount === 0 ? (
            <Chip size='small' color='success' label='Keine Abweichungen' />
          ) : (
            <Chip
              size='small'
              color='warning'
              label={
                differenceCount === 1
                  ? '1 Abweichung'
                  : `${differenceCount} Abweichungen`
              }
            />
          )}
        </Box>

        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Feld</TableCell>
              <TableCell>{renderHeadCell(left)}</TableCell>
              <TableCell>{renderHeadCell(right)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderSection('Person', 'Person', PERSON_LABEL)}
            {renderSection('Adresse', 'Address', ADDRESS_LABEL)}
            {renderSection('Kontakt', 'Contact', CONTACT_LABEL)}
          </TableBody>
        </Table>

        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ display: 'block', mt: 2 }}
        >
          Die Ausweisfotos werden nach der Übernahme aus der Erfassung entfernt
          und liegen als Dokument beim Beteiligten — die erfassten Daten bleiben
          hier dauerhaft nachvollziehbar.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
